import { useRequest } from '@features/request/useRequest';
import { useResetStoreData } from '@features/request/useResetStoreData';
import { SortEnumType } from '@gql/graphql';
import { useAgGridDataSource } from '@hooks/useAgGridDataSource';

import type { SortChangedEvent } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { catalogUERBaseCostItemsGql } from '../../../CatalogUERBaseCost/catalogUerBaseCostQueries';
import { getOrderValues } from '../../AddRatiosModal/helpers';
import type { TOrderState } from '../../AddRatiosModal/types';
import { getWhereColumnFilters } from '../helpers';

interface IUseMassApplicationBaseCostData {
    filterValues: Record<string, string[]>;
    id: string | undefined;
}

const requestId = 'massApplicationBaseCostsData';

export const useMassApplicationBaseCostData = ({ filterValues, id }: IUseMassApplicationBaseCostData) => {
    const [order, setOrder] = useState<TOrderState | TOrderState[] | undefined>({ code: SortEnumType.Asc });

    const [baseCostsReset] = useResetStoreData({ requestId });

    const reset = useCallback(() => {
        baseCostsReset(undefined);
    }, [baseCostsReset]);

    const variables = useMemo(
        () =>
            id
                ? {
                      catalogUniqueId: id,

                      where: getWhereColumnFilters(filterValues),
                      order,
                  }
                : undefined,
        [filterValues, id, order],
    );

    const changeOrderHandler = useCallback((params: SortChangedEvent) => {
        const columnsState = params?.api?.getColumnState();

        const { sort, colId } = columnsState?.find((item) => item.sort !== null) ?? {};

        setOrder((prevState) => getOrderValues(sort, colId, prevState));
        // делается второй вызов, чтобы в запрос отправлялось уже обновленное значение сортировки,
        // иначе почему-то не работает
        setOrder((prevState) => getOrderValues(sort, colId, prevState));
    }, []);

    const [massApplicationBaseCostData, , , massApplicationBaseCostRequest] = useRequest({
        requestId,
        query: catalogUERBaseCostItemsGql,
        variables,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: massApplicationBaseCostRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (massApplicationBaseCostData?.catalogUERBaseCostItems?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                massApplicationBaseCostData?.catalogUERBaseCostItems?.items ?? [],
                massApplicationBaseCostData?.catalogUERBaseCostItems?.totalCount,
            );
        }
    }, [
        massApplicationBaseCostData?.catalogUERBaseCostItems?.items,
        massApplicationBaseCostData?.catalogUERBaseCostItems?.totalCount,
        getRowParams,
    ]);

    useEffect(() => {
        reset();
    }, [reset]);

    return useMemo(
        () => ({
            dataSource,
            data: massApplicationBaseCostData?.catalogUERBaseCostItems?.items ?? [],
            changeOrderHandler,
            reset,
        }),
        [changeOrderHandler, dataSource, massApplicationBaseCostData?.catalogUERBaseCostItems?.items, reset],
    );
};
